import { z } from 'zod'
import { zfd } from 'zod-form-data'
import { PROPERTY_SUBTYPE_MAP, TYPES, fmtNumberAbbr } from '~/utils'
import { type ModelOrganization } from './organization'
import { type ModelUser } from './user'
import { regexEmail, regexPhone } from './utils'

export enum MemberRequestStatus {
  open = 'open',
  pending = 'pending',
  filled = 'filled',
  draft = 'draft',
  expired = 'expired',
}

export enum MemberRequestListingType {
  sale = 'sale',
  lease = 'lease',
  saleLease = 'saleLease',
}

export enum MemberRequestType {
  member = 'member',
  ecDev = 'ecDev',
}

export type MemberRequestMeta = {
  minSf?: number
  maxSf?: number
  maxAcres?: number
  minAcres?: number
  occupancyQuarter?: string
  occupancyYear?: string
  office?: number
  ceilingHeightMin?: number
  powerDescription?: string
  waterDescription?: string
  sewerDescription?: string
  naturalGasDescription?: string
  hvacDescription?: string
  loadingDescription?: string
  fiberOpticDescription?: string
  fireProtectionDescription?: string
  lightingDescription?: string
  parkingDescription?: string
  notes?: string
}

export type MemberRequest = {
  _id: string
  tid: string
  slug: string
  created: string
  modified: string
  deleted?: boolean
  deletedDate?: string
  company?: {
    _id: string
    tid: string
    name: string
    slug: string
  }
  organization: ModelOrganization
  user?: ModelUser
  name: string
  status?: MemberRequestStatus
  type: MemberRequestListingType
  requestType: MemberRequestType
  propertyTypes: string[]
  propertySubtypes?: string[]
  developmentOrganization?: string
  region?: string
  contactFirstName?: string
  contactLastName?: string
  contactNumber?: string
  contactEmail?: string
  meta?: MemberRequestMeta
}

export const MemberRequestSchema = z
  .object({
    _id: z.string(),
    tid: z.string(),
    created: z.string(),
    modified: z.string(),
    name: zfd.text(z.string().min(1)),
    slug: zfd.text(z.string()),
    deleted: z.boolean().optional(),
    deletedDate: z.string().optional(),
    company: z
      .object({
        _id: z.string(),
        tid: z.string(),
        name: z.string(),
        slug: z.string(),
      })
      .optional(),
    organization: z.custom<ModelOrganization>(),
    user: z.custom<ModelUser>(),
    status: z.nativeEnum(MemberRequestStatus),
    type: z.nativeEnum(MemberRequestListingType),
    requestType: z.nativeEnum(MemberRequestType),
    propertyTypes: zfd.repeatable(
      z.array(z.string()).min(1, 'Choose at least one Property Type')
    ),
    propertySubtypes: zfd.repeatable(z.array(z.string()).optional()),
    developmentOrganization: zfd.text(z.string().min(1).optional()),
    region: zfd.text(z.string().min(1).optional()),
    contactFirstName: zfd.text(z.string().min(1).optional()),
    contactLastName: zfd.text(z.string().min(1).optional()),
    contactEmail: z
      .string()
      .optional()
      .refine((val) => regexEmail(val), {
        message: 'Please enter a valid email',
      }),
    contactNumber: z
      .string()
      .optional()

      .refine((val) => regexPhone(val), {
        message: 'Please enter a valid phone number.',
      }),

    meta: z.object({
      minSf: zfd.numeric(z.number().nonnegative().optional()),
      maxSf: zfd.numeric(z.number().nonnegative().optional()),
      maxAcres: zfd.numeric(z.number().nonnegative().optional()),
      minAcres: zfd.numeric(z.number().nonnegative().optional()),
      occupancyQuarter: z.string().optional(),
      occupancyYear: z.string().optional(),
      office: zfd.numeric(z.number().nonnegative().optional()),
      ceilingHeightMin: zfd.numeric(z.number().nonnegative().optional()),
      powerDescription: zfd.text(z.string().optional()),
      waterDescription: zfd.text(z.string().optional()),
      sewerDescription: zfd.text(z.string().optional()),
      naturalGasDescription: zfd.text(z.string().optional()),
      hvacDescription: zfd.text(z.string().optional()),
      loadingDescription: zfd.text(z.string().optional()),
      fiberOpticDescription: zfd.text(z.string().optional()),
      fireProtectionDescription: zfd.text(z.string().optional()),
      lightingDescription: zfd.text(z.string().optional()),
      parkingDescription: zfd.text(z.string().optional()),
      notes: zfd.text(z.string().optional()),
    }),
  })
  .refine(
    (data) => {
      if (data.requestType === 'ecDev' && !data.developmentOrganization) {
        return false
      }
      return true
    },
    {
      message: 'Please enter an organization name',
      path: ['developmentOrganization'],
    }
  )
  .refine(
    (data) => {
      if (data.requestType === 'ecDev' && !data.contactFirstName) {
        return false
      }
      return true
    },
    {
      message: 'Please enter a first name',
      path: ['contactFirstName'],
    }
  )
  .refine(
    (data) => {
      if (data.requestType === 'ecDev' && !data.contactLastName) {
        return false
      }
      return true
    },
    {
      message: 'Please enter a last name',
      path: ['contactLastName'],
    }
  )
  .refine(
    (data) => {
      if (data.requestType === 'ecDev' && !data.contactNumber) {
        return false
      }
      return true
    },
    {
      message: 'Please enter a phone number',
      path: ['contactNumber'],
    }
  )
  .refine(
    (data) => {
      if (data.requestType === 'ecDev' && !data.contactEmail) {
        return false
      }
      return true
    },
    {
      message: 'Please enter an email',
      path: ['contactEmail'],
    }
  ) satisfies z.ZodType<MemberRequest>

export function getMemberRequestLabels(memberRequest: MemberRequest) {
  const labels: string[] = [
    TYPES[memberRequest.type],
    memberRequest.propertyTypes
      .map((propertyType) => {
        if (memberRequest.propertySubtypes) {
          const subTypes = PROPERTY_SUBTYPE_MAP[propertyType]?.filter((sub) =>
            memberRequest.propertySubtypes!.includes(sub)
          )
          if (subTypes?.length > 0) {
            return `${propertyType} (${subTypes.join(', ')})`
          }
        }
        return propertyType
      })
      .join(', '),
  ]
  if (memberRequest.meta?.minSf && memberRequest.meta?.maxSf) {
    labels.push(
      `${fmtNumberAbbr(memberRequest.meta.minSf)} - ${fmtNumberAbbr(memberRequest.meta?.maxSf)} SF`
    )
  } else if (memberRequest.meta?.minSf) {
    labels.push(`> ${fmtNumberAbbr(memberRequest.meta.minSf)} SF`)
  } else if (memberRequest.meta?.maxSf) {
    labels.push(`< ${fmtNumberAbbr(memberRequest.meta?.maxSf)} SF`)
  }
  if (memberRequest.meta?.minAcres && memberRequest.meta?.maxAcres) {
    labels.push(
      `${fmtNumberAbbr(memberRequest.meta?.minAcres)} - ${fmtNumberAbbr(memberRequest.meta?.maxAcres)} ac`
    )
  } else if (memberRequest.meta?.minAcres) {
    labels.push(`> ${fmtNumberAbbr(memberRequest.meta?.minAcres)} ac`)
  } else if (memberRequest.meta?.maxAcres) {
    labels.push(`< ${fmtNumberAbbr(memberRequest.meta?.maxAcres)} ac`)
  }
  return labels
}
